/**
 * ThemeToggle — sun/moon icon button used in the top bar.
 *
 * Reads/writes the Zustand theme store. We render both icons stacked and
 * cross-fade them so the click feels tactile (no layout shift).
 */
import { Moon, Sun } from "lucide-react";
import { useTheme } from "@/stores/theme";
import { cn } from "@/lib/cn";

export interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const resolved = useTheme((s) => s.resolved);
  const toggle = useTheme((s) => s.toggle);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={resolved === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      title={resolved === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      className={cn(
        "relative inline-flex h-9 w-9 items-center justify-center rounded-lg",
        "border border-line bg-surface text-fg-dim",
        "transition-colors hover:text-fg hover:bg-surface-2",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50",
        className,
      )}
    >
      <Sun
        size={16}
        className={cn(
          "absolute transition-all duration-200",
          resolved === "dark" ? "opacity-0 -rotate-90 scale-50" : "opacity-100 rotate-0 scale-100",
        )}
      />
      <Moon
        size={16}
        className={cn(
          "absolute transition-all duration-200",
          resolved === "dark" ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-90 scale-50",
        )}
      />
    </button>
  );
}
